import React, { useCallback, useEffect, useState, memo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Box, TextInput, Text, CheckBox, Button, Form } from 'grommet';
import { Add, Subtract } from 'grommet-icons';
import styled from 'styled-components';

import IconSearch from '../Icons/IconSearch';
import InputRangeSlider from '../Inputs/InputRangeSlider';
import ColorFilterPanelGroup from './ColorFilterPanelGroup';

import {
  toggleFilterTag,
  toggleSort,
  resetFilterTags,
  setFilterQuery,
} from '../../state/color/colorSlice';
import {
  setCollectionZoom,
  incrementZoom,
} from '../../state/collection/collectionSlice';

const Container = styled(Box)`
  flex-direction: column;
  @media only screen ${(p) =>
      p.theme.global.breakpoints.small.value &&
      `and (min-width: ${p.theme.global.breakpoints.small.value}px)`} {
    flex-direction: column-reverse;
  }
`;

const ZoomContainer = styled(Box)``;

const CheckBoxContainer = styled(Box)`
  align-items: center;
  @media only screen ${(p) =>
      p.theme.global.breakpoints.small.value &&
      `and (min-width: ${p.theme.global.breakpoints.small.value}px)`} {
    align-items: flex-start;
  }
`;

const RangeButton = styled(Button)`
  padding: ${(p) => p.theme.global.edgeSize.small};
`;
const SearchInput = styled(TextInput)`
  @media only screen ${(p) =>
      p.theme.global.breakpoints.small.value &&
      `and (min-width: ${p.theme.global.breakpoints.small.value}px)`} {
    border-bottom: ${(p) => p.theme.global.borderSize.small} solid #000;
  }
`;
const SearchContainer = styled(Box)`
  border-bottom: ${(p) => p.theme.global.borderSize.small} solid #000;
  @media only screen ${(p) =>
      p.theme.global.breakpoints.small.value &&
      `and (min-width: ${p.theme.global.breakpoints.small.value}px)`} {
    border-bottom: none;
  }
`;
const FilterGroupContainer = styled(Box)`
  border-bottom: ${(p) => p.theme.global.borderSize.small} solid #000;
  margin-bottom: ${(p) => p.theme.global.edgeSize.medium};
  @media only screen ${(p) =>
      p.theme.global.breakpoints.small.value &&
      `and (min-width: ${p.theme.global.breakpoints.small.value}px)`} {
    border-bottom: none;
  }
`;

const ColorFilterPanel = ({
  filterGroups,
  searchOptions,
  onFilter,
  onSearch,
  onReset,
  onSort,
  activeFilters = [],
  activeSorts = [],
  baseUrl,
  productCount,
  disableZoom = false,
  pad = 'medium',
  hideFilters,
  ...rest
}) => {
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState('');

  const filterTags = useSelector((state) => [
    ...state.color.filterTags,
    ...activeFilters,
  ]);

  const stateSorts = useSelector((state) => state.color.sorts);
  const collectionZoom = useSelector((state) => state.collection.zoom);
  const sorts = [...activeSorts, ...stateSorts];

  const handleSearch = useCallback(
    (event) => {
      const { value: newValue } = event.target;
      setSearchQuery(newValue);
      if (onSearch) {
        onSearch(newValue);
      } else {
        if (!newValue.trim()) {
          dispatch(setFilterQuery(''));
        } else {
          dispatch(setFilterQuery(newValue));
        }
      }
    },
    [onSearch, setSearchQuery]
  );

  const dispatchHandleTag = useCallback(
    (tag) => {
      dispatch(toggleFilterTag(tag));
    },
    [toggleFilterTag, dispatch]
  );
  const handleTag = onFilter ? onFilter : dispatchHandleTag;

  const dispatchHandleSort = useCallback(
    (sort) => {
      dispatch(toggleSort(sort));
    },
    [toggleSort, dispatch]
  );
  const handleSort = onSort ? onSort : dispatchHandleSort;

  const dispatchHandleReset = useCallback(
    () => dispatch(resetFilterTags()),
    [resetFilterTags, dispatch]
  );
  const handleReset = onReset ? onReset : dispatchHandleReset;

  // Cleanup on exit
  useEffect(() => {
    return () => {
      dispatch(setFilterQuery(''));
    };
  }, []);

  useEffect(() => {
    if (collectionZoom === 1 && sorts.length === 0) {
      handleSort({ slug: 'colorIndex' });
    }
  }, [collectionZoom]);

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
      }}
      style={{ width: '100%' }}
    >
      <Box gap="medium" fill="vertical">
        <SearchContainer pad={pad}>
          <label>
            <Box fill="vertical" direction="row" align="center">
              <SearchInput
                size="small"
                label="Search"
                name="search"
                placeholder="Search color name"
                onChange={handleSearch}
                plain
                icon={<IconSearch color="black" size="20px" />}
                value={searchQuery}
              />
            </Box>
          </label>
        </SearchContainer>
        <Container flex={false} {...rest}>
          {!hideFilters && (
            <FilterGroupContainer>
              {filterGroups.map((x) => (
                <ColorFilterPanelGroup
                  activeFilters={filterTags}
                  activeSorts={sorts}
                  onTagFilter={handleTag}
                  onSort={handleSort}
                  key={x.title}
                  open={true}
                  baseUrl={baseUrl}
                  onResetFilters={handleReset}
                  resultsCount={productCount}
                  searchOptions={searchOptions}
                  filterGroups={filterGroups}
                  onSearch={handleSearch}
                  pad={pad}
                  {...x}
                />
              ))}
            </FilterGroupContainer>
          )}

          <ZoomContainer pad={pad} justify="center" align="center" gap="medium">
            {!disableZoom && (
              <Box
                width={{ max: 'medium' }}
                flex={true}
                fill="horizontal"
                direction="row-responsive"
                align="center"
                justify="center"
                gap="small"
              >
                <Text size="small" textAlign="center" weight={500}>
                  Zoom
                </Text>
                <Box direction="row">
                  <RangeButton
                    size="small"
                    icon={<Subtract size="small" color="black" />}
                    onClick={() => dispatch(incrementZoom(-1))}
                    a11yTitle="Zoom out"
                  />
                  <InputRangeSlider
                    onChange={(event) => {
                      dispatch(setCollectionZoom(event.target.value));
                    }}
                    value={collectionZoom}
                    min={1}
                    max={3}
                    step={1}
                  />

                  <RangeButton
                    size="small"
                    icon={<Add size="small" color="black" />}
                    onClick={() => dispatch(incrementZoom(1))}
                    a11yTitle="Zoom in"
                  />
                </Box>
              </Box>
            )}
            <CheckBoxContainer fill="horizontal">
              <CheckBox
                size="small"
                label={
                  <Text
                    size="small"
                    weight={500}
                    style={{ lineHeight: '23px' }}
                  >
                    Sort Light to Dark
                  </Text>
                }
                reverse
                checked={
                  sorts.filter(
                    (x) => x.id === 'colorIndex' || x.slug === 'colorIndex'
                  ).length > 0
                }
                onChange={() => handleSort({ slug: 'colorIndex' })}
              />
            </CheckBoxContainer>
          </ZoomContainer>
        </Container>
      </Box>
    </Form>
  );
};

ColorFilterPanel.propTypes = {
  filterGroups: PropTypes.array,
  searchOptions: PropTypes.array,
  onFilter: PropTypes.func,
  onSearch: PropTypes.func,
  onReset: PropTypes.func,
  onSort: PropTypes.func,
  activeFilters: PropTypes.array,
  activeSorts: PropTypes.array,
  stickyTop: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  baseUrl: PropTypes.string,
  productCount: PropTypes.number,
  disableZoom: PropTypes.bool,
  pad: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default memo(ColorFilterPanel);
